import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChartColumn,
  ChevronDown,
  ChevronUp,
  Link2,
  Lock,
} from "lucide-react";
import moment from "moment-timezone";
import { FlashingValue } from "src/common/FlashingValue";
import Image from "next/image";
import ListModal from "src/components/dashboard/listModal";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import BriefCaseFilledIcon from "src/assets/images/Briefcase-Fiilled.svg";
import Briefcase from "src/assets/images/Briefcase.svg";
import hotFlame from "src/assets/images/fire.png";
import InvestmentIcon from "src/assets/images/investment-analysis-icon.svg";
import WatchlistFilledIcon from "src/assets/images/WatchList-Filled.svg";
import WatchlistIcon from "src/assets/images/Watchlist.svg";
import AppContext from "src/components/AppContext";
import ConfirmationModal from "src/common/ConfirmationModal";
import SnowflakeIcon from "src/common/SnowflakeIcon";
import FreeTrialRestrictionModal from "src/common/freeTrialRestrictionModal";
import { useSocket } from "src/components/SocketContext";
import RetestOrderSkeleton from "src/components/skeleton/RetestOrderSkeleton";
import {
  fetchWrapper,
  getHotMonths,
  OrderBlockForRetest,
  StockTableData,
  Watchlist,
  winLossPoint,
} from "src/helpers";
import {
  formatDate,
  getLocalStorageUser,
} from "src/helpers/api/sharedFunction";
import { formatCurrencyWithLocalString } from "src/helpers/numberFormatter";
import { StockSearchInput, useStockSearch } from "src/hooks/useStockSearchHook";
import { alertService } from "src/services";
import WinLossChartModal from "./chartModal";
import SupplyDemandTable from "./SupplyDemandTable";
import DialogModal from "src/common/DialogModal";

type SortDirection = "asc" | "desc" | null;
type SortConfig = {
  key: string;
  direction: SortDirection;
};

type LtpMessage = {
  symbol_name: string;
  ltp: string;
  close: string;
  high?: string;
  symbol_token?: string;
  ltp_date?: string;
};

type ExtendedOrderBlock = OrderBlockForRetest &
  Partial<{
    ltp: number;
    close: number;
    lastUpdated: number;
    added_to_portfolio: boolean;
    added_to_watchlist: boolean;
  }>;

interface RetestOrderProps {
  data: OrderBlockForRetest[];
  onRowSelect?: (row: OrderBlockForRetest) => void;
  selectedRow?: { symbol: string; ob_date?: string };
  className?: string;
  setPastderivative15: Dispatch<SetStateAction<boolean>>;
  pastDerivative15: boolean;
  onWatchlistUpdate?: (symbol: string) => void;
  onWatchlistRemove?: (symbol: string) => void;
  onPortfolioUpdate?: (symbol: string) => void;
  loading?: boolean;
}

interface RetestRowProps {
  row: ExtendedOrderBlock;
  isSelected: boolean;
  isTrialUser: boolean;
  onRowSelect: (row: OrderBlockForRetest) => void;
  onPortfolioClick: (e: React.MouseEvent, row: ExtendedOrderBlock) => void;
  onWatchlistClick: (e: React.MouseEvent, row: ExtendedOrderBlock) => void;
  onWinLossClick: (e: React.MouseEvent, row: ExtendedOrderBlock) => void;
  onTrialRestrict: () => void;
}

const RetestRow = React.memo(function RetestRow({
  row,
  isSelected,
  isTrialUser,
  onRowSelect,
  onPortfolioClick,
  onWatchlistClick,
  onWinLossClick,
  onTrialRestrict,
}: RetestRowProps) {
  const rowSymbol = row.symbol.replace(/\.NS$/, "");

  return (
    <tr
      id={`row-${rowSymbol}-retest`}
      className={`border-b border-[#d0d4e4] dark:border-[#394564] hover:bg-gray-700/20 transition-colors cursor-pointer ${
        isSelected
          ? "border-l-[2px] border-l-[#00a85f] dark:border-l-[#00a85f] bg-[#edf1fc] dark:bg-[#00225580]"
          : ""
      }`}
      onClick={() => {
        if (!isTrialUser) onRowSelect(row);
      }}
    >
      <td
        className="py-1 px-2 text-xs relative cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          if (isTrialUser) {
            onTrialRestrict();
          } else {
            onRowSelect(row);
          }
        }}
      >
        <div className="relative flex items-center gap-1">
          <span className="w-4 h-4 flex items-center gap-1 flex-shrink-0">
            {row?.hot_flame && (
              <div className="relative group">
                <Image
                  src={hotFlame}
                  alt="hot flame"
                  className={`w-4 h-4 transition-transform dark:group-hover:scale-110 ${
                    isTrialUser
                      ? "opacity-30 blur-[2.5px] pointer-events-none select-none"
                      : ""
                  }`}
                />
                {!isTrialUser && (
                  <div className="absolute z-10 hidden w-80 top-[-60px] translate-y-0 sm:w-max sm:top-1/2 sm:-translate-y-1/2 group-hover:block bg-[#30324e] text-white text-sm p-2 rounded-md shadow-lg border border-[#394564] left-full ml-0 sm:ml-2">
                    This stock is identified as a high performer based on its
                    2-months performance trend. (
                    {getHotMonths(moment(row.retest_date)).join(", ")})
                    <div className="absolute right-full top-1/2 transform -translate-y-1/2 w-0 h-0 border-t-4 border-t-transparent border-b-4 border-b-transparent border-r-4 border-r-[#30324e]"></div>
                  </div>
                )}
              </div>
            )}
            {row?.snow_flake && <SnowflakeIcon isTrialUser={isTrialUser} />}
          </span>
          <span
            className={`transition-all duration-300 ${
              isTrialUser
                ? "opacity-30 blur-[2.5px] pointer-events-none select-none"
                : ""
            }`}
          >
            {rowSymbol}
          </span>
          {isTrialUser && (
            <div className="absolute right-0 flex items-center justify-center w-5 h-5 rounded-full bg-gray-200/90 dark:bg-gray-700/90 border border-gray-400 dark:border-gray-600 shadow-md">
              <Lock className="w-3 h-3 text-gray-700 dark:text-gray-300" />
            </div>
          )}
        </div>
      </td>
      <td className="py-2 px-2 text-xs text-right min-w-[150px]">
        <FlashingValue
          value={
            row.ltp ? formatCurrencyWithLocalString(row.ltp.toFixed(2)) : "--"
          }
          isPositive={
            row.ltp && row.close && row.ltp - row.close === 0
              ? null
              : !(row.ltp && row.close && row.ltp - row.close < 0)
          }
          flashTrigger={row.lastUpdated}
          className="text-xs"
        />{" "}
        &nbsp;
        <span
          className={`${
            row.ltp && row.close && row.ltp - row.close === 0
              ? "text-gray-900 dark:text-white"
              : row.ltp && row.close && row.ltp - row.close < 0
                ? "dark:text-red-400 text-red-600"
                : "dark:text-green-400 text-green-600"
          }`}
        >
          (
          {row.close && row.ltp
            ? (((row.ltp - row.close) / row.close) * 100).toFixed(2)
            : "0.00"}
          %)
        </span>
      </td>
      <td className="py-1 px-1 text-xs text-center dark:group-hover:bg-[#394564]/50 transition-colors duration-200">
        {formatDate(row.ob_date)}
      </td>
      <td className="py-1 px-1 text-xs text-center dark:group-hover:bg-[#394564]/50 transition-colors duration-200">
        {formatDate(row.retest_date)}
      </td>
      <td className="py-1 px-1 text-xs dark:group-hover:bg-[#394564]/50 transition-colors duration-200 text-center">
        <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full dark:bg-blue-900 dark:text-blue-300 text-center">
          {row.no_of_days}
        </span>
      </td>
      <td className="py-1 px-4 sm:px-6 text-xs dark:group-hover:bg-[#394564]/50 transition-colors duration-200 text-center">
        <div className="flex gap-2 items-center justify-center">
          <span
            className={`px-0 py-0 rounded-full text-xs font-medium ${
              row.side === "supply"
                ? "text-red-800 dark:text-red-300"
                : "text-green-800 dark:text-green-300"
            }`}
          >
            {row.side === "supply" ? <ChevronDown /> : <ChevronUp />}
          </span>
          <span
            className={`px-1 py-0.5 rounded-full text-xs font-medium min-w-[80px] ${
              row.side === "supply"
                ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
            }`}
          >
            {row.side}
          </span>
        </div>
      </td>
      <td className="py-1 px-1 text-xs dark:group-hover:bg-[#394564]/50 transition-colors duration-200">
        <div className="flex items-center justify-center">
          <button
            disabled={isTrialUser}
            onClick={(e) => onWinLossClick(e, row)}
            className={`p-1 ${isTrialUser ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
          >
            <ChartColumn className="w-4 h-4" />
          </button>
        </div>
      </td>
      <td className="py-1 px-1 dark:group-hover:bg-[#394564]/50 transition-colors duration-200">
        <div className="flex items-center justify-center space-x-2">
          <button
            title="Add to Portfolio"
            disabled={isTrialUser}
            onClick={(e) => onPortfolioClick(e, row)}
            className={`rounded-full p-1 transition-all duration-200 ${
              isTrialUser ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            }`}
          >
            <Image
              src={row?.added_to_portfolio ? BriefCaseFilledIcon : Briefcase}
              alt="Briefcase"
              style={{
                height: "auto",
                filter: row?.added_to_portfolio
                  ? "invert(62%) sepia(66%) saturate(1200%) hue-rotate(350deg) brightness(100%) contrast(95%)"
                  : "",
              }}
              className={`w-5 h-5 transition-all duration-200 ${
                row?.added_to_portfolio
                  ? "filter-orange"
                  : "filter invert dark:invert-0 dark:brightness-100"
              }`}
            />
          </button>
          <button
            title="Add to Watchlist"
            disabled={isTrialUser}
            onClick={(e) => onWatchlistClick(e, row)}
            className={`rounded-full p-1 transition-all duration-200 ${
              isTrialUser ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            }`}
          >
            <Image
              src={
                row?.added_to_watchlist ? WatchlistFilledIcon : WatchlistIcon
              }
              alt="Watchlist"
              style={{
                filter: row?.added_to_watchlist
                  ? "invert(62%) sepia(66%) saturate(1200%) hue-rotate(350deg) brightness(100%) contrast(95%)"
                  : "",
              }}
              className={`w-5 h-5 transition-all duration-200 ${
                row?.added_to_watchlist
                  ? "filter-orange"
                  : "filter invert dark:invert-0 dark:brightness-100"
              }`}
            />
          </button>
        </div>
      </td>
    </tr>
  );
});

const RetestOrderTable = ({
  data,
  onRowSelect,
  selectedRow,
  className = "",
  setPastderivative15,
  pastDerivative15,
  onWatchlistUpdate,
  onWatchlistRemove,
  onPortfolioUpdate,
  loading = false,
}: RetestOrderProps) => {
  const [showWinLossModal, setShowWinLossModal] = useState(false);
  const context = useContext(AppContext);
  const { darkMode } = context.state;
  const [winLossRatioData, setWinLossRatioData] = useState<winLossPoint[]>([]);
  const [winLossLoader, setWinLossLoader] = useState<boolean>(false);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "",
    direction: null,
  });
  const [selectedSymbolName, setSelectedSymbolName] = useState<string>("");
  const user = getLocalStorageUser() || {};
  const token = user?.token;
  const [records, setRecords] = useState<ExtendedOrderBlock[]>(
    data as ExtendedOrderBlock[],
  );
  const isTrialUser = user?.is_trial === true;
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [supplyDemandModalOpen, setSupplyDemandModalOpen] = useState(false);
  const [watchlist, setWatchlist] = useState<Watchlist>({});
  const [selectedStock, setSelectedStock] = useState<StockTableData | null>(
    null,
  );
  const [freeTrialRestrictModal, setFreeTrialRestrictModal] = useState(false);
  const [
    freeTrialRestrictModalPastOrderBlock,
    setFreeTrialRestrictModalPastOrderBlock,
  ] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [notes, setNotes] = useState("");
  const [quantity, setQuantity] = useState<number>(1);
  const [buyPrice, setBuyPrice] = useState<number>(0);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const {
    searchInput,
    filteredSymbols,
    showDropdown,
    setShowDropdown,
    onSearchChange,
    handleSuggestionClick,
  } = useStockSearch(
    records,
    selectedRow,
    onRowSelect,
    "retest-order-search-dropdown",
    "retest",
  );

  const onRowSelectRef = useRef(onRowSelect);
  const onWatchlistUpdateRef = useRef(onWatchlistUpdate);
  const onWatchlistRemoveRef = useRef(onWatchlistRemove);
  const onPortfolioUpdateRef = useRef(onPortfolioUpdate);

  onRowSelectRef.current = onRowSelect;
  onWatchlistUpdateRef.current = onWatchlistUpdate;
  onWatchlistRemoveRef.current = onWatchlistRemove;
  onPortfolioUpdateRef.current = onPortfolioUpdate;

  const stableOnRowSelect = useCallback((row: OrderBlockForRetest) => {
    onRowSelectRef.current?.(row);
  }, []);

  const updatedStock = useCallback(
    (stockSymbol: string, isWatchlist?: boolean) => {
      if (!stockSymbol) return;
      const normalizedSymbol = stockSymbol.replace(/\.NS$/, "");
      setRecords((prevRecords) =>
        prevRecords.map((item) =>
          item.symbol.replace(/\.NS$/, "") === normalizedSymbol
            ? {
                ...item,
                [isWatchlist ? "added_to_watchlist" : "added_to_portfolio"]:
                  true,
              }
            : item,
        ),
      );
      if (isWatchlist) {
        onWatchlistUpdateRef.current?.(stockSymbol);
      } else {
        onPortfolioUpdateRef.current?.(stockSymbol);
      }
    },
    [],
  );

  const handleResetField = useCallback(() => {
    setWatchlist({ symbol: "", is_recurring: "" });
    setNotes("");
    setQuantity(0);
    setBuyPrice(0);
  }, []);

  const openModal = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (stock: any) => {
      setSelectedDate(new Date(stock.retest_touch_date));
      setSelectedStock(stock);
      setQuantity(1);
      setBuyPrice(Number(stock.ltp));
      setNotes("");
      setIsModalOpen(true);
    },
    [],
  );

  const openWatchlistModal = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (stock: any) => {
      setSelectedStock(stock);
      if (!stock?.added_to_watchlist) {
        setIsOpen(true);
      } else {
        setConfirmationModal(true);
      }
      setWatchlist({
        symbol: stock.symbol,
        is_recurring: "",
      });
    },
    [],
  );

  const tokenRef = useRef(token);
  tokenRef.current = token;

  const getWinLossRatioData = useCallback(async (symbol: string) => {
    try {
      setWinLossLoader(true);
      const response = await fetchWrapper.get(
        `/api/dashboard/seasonal/winloss_ratio?symbol=${symbol.replace(/\.NS$/, "")}`,
        undefined,
        {
          Authorization: `Bearer ${tokenRef.current}`,
        },
      );
      setWinLossRatioData(response?.data);
    } finally {
      setWinLossLoader(false);
    }
  }, []);

  const handlePortfolioClick = useCallback(
    (e: React.MouseEvent, row: ExtendedOrderBlock) => {
      e.stopPropagation();
      const rowSymbol = row.symbol.replace(/\.NS$/, "");
      openModal({ ...row, symbol: rowSymbol });
    },
    [openModal],
  );

  const handleWatchlistClick = useCallback(
    (e: React.MouseEvent, row: ExtendedOrderBlock) => {
      e.stopPropagation();
      const rowSymbol = row.symbol.replace(/\.NS$/, "");
      openWatchlistModal({ ...row, symbol: rowSymbol });
    },
    [openWatchlistModal],
  );

  const handleWinLossClick = useCallback(
    (e: React.MouseEvent, row: ExtendedOrderBlock) => {
      e.stopPropagation();
      const rowSymbol = row.symbol.replace(/\.NS$/, "");
      getWinLossRatioData(row.symbol);
      setSelectedSymbolName(rowSymbol);
      setShowWinLossModal(true);
    },
    [getWinLossRatioData],
  );

  const handleTrialRestrict = useCallback(() => {
    setFreeTrialRestrictModal(true);
  }, []);

  const { emit, on, off, isConnected } = useSocket();
  const updateBufferRef = useRef<Map<string, { ltp: number; close: number }>>(
    new Map(),
  );

  useEffect(() => {
    const flushInterval = setInterval(() => {
      const buffer = updateBufferRef.current;
      if (buffer.size === 0) return;

      const snapshot = new Map(buffer);
      buffer.clear();
      const now = Date.now();

      setRecords((prev) => {
        let hasAnyChange = false;
        const next = prev.map((stock) => {
          const key = stock.symbol.replace(/\.NS$/, "");
          const update = snapshot.get(key);
          if (!update) return stock;
          if (stock.ltp === update.ltp && stock.close === update.close)
            return stock;
          hasAnyChange = true;
          return {
            ...stock,
            ltp: update.ltp,
            close: update.close,
            lastUpdated: now,
          };
        });
        return hasAnyChange ? next : prev;
      });
    }, 500);

    return () => clearInterval(flushInterval);
  }, []);

  const handleStockUpdate = useCallback((message: LtpMessage[]) => {
    for (const m of message) {
      const ltp = parseFloat(m.ltp);
      const close = parseFloat(m.close);
      if (!isNaN(ltp)) {
        updateBufferRef.current.set(m.symbol_name, { ltp, close });
      }
    }
  }, []);

  useLayoutEffect(() => {
    setRecords(data);
  }, [data]);

  const symbolsKey = useMemo(
    () =>
      records
        .map((r) => r.symbol.replace(/\.NS$/, ""))
        .sort()
        .join(","),
    [records],
  );

  useEffect(() => {
    if (!symbolsKey || !isConnected) return;
    const symbols = symbolsKey.split(",");
    on("stock_update", handleStockUpdate);
    emit("subscribe_stock", { symbol: symbols });

    return () => {
      off("stock_update", handleStockUpdate);
      emit("unsubscribe_stock", { symbol: symbols });
    };
  }, [symbolsKey, isConnected, on, off, emit, handleStockUpdate]);

  const sortedData = useMemo(() => {
    const sortableData = [...records];
    if (!sortConfig.key || !sortConfig.direction) return sortableData;

    return sortableData.sort((a, b) => {
      const { key, direction } = sortConfig;
      if (key === "no_of_days") {
        return direction === "asc"
          ? a.no_of_days - b.no_of_days
          : b.no_of_days - a.no_of_days;
      }

      if (key === "percentage") {
        const aPercent =
          a.ltp && a.close ? ((a.ltp - a.close) / a.close) * 100 : 0;
        const bPercent =
          b.ltp && b.close ? ((b.ltp - b.close) / b.close) * 100 : 0;
        return direction === "asc" ? aPercent - bPercent : bPercent - aPercent;
      }

      if (key === "ob_date" || key === "retest_touch_date") {
        const dateA = new Date(a[key].split("/").reverse().join("-"));
        const dateB = new Date(b[key].split("/").reverse().join("-"));
        return direction === "asc"
          ? dateA.getTime() - dateB.getTime()
          : dateB.getTime() - dateA.getTime();
      }

      const aValue = (a[key as keyof typeof a] ?? "") as string | number;
      const bValue = (b[key as keyof typeof b] ?? "") as string | number;

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [records, sortConfig]);

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        let newDirection: SortDirection = null;
        if (prev.direction === null) newDirection = "desc";
        else if (prev.direction === "desc") newDirection = "asc";
        else if (prev.direction === "asc") newDirection = null;
        return { key, direction: newDirection };
      }
      return { key, direction: "desc" };
    });
  };

  const getSortIcon = (key: string) => {
    if (sortConfig.key !== key)
      return (
        <ArrowUpDown className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
      );
    if (sortConfig.direction === "asc")
      return <ArrowUp className="w-3 h-3 ml-1" />;
    if (sortConfig.direction === "desc")
      return <ArrowDown className="w-3 h-3 ml-1" />;
    return (
      <ArrowUpDown className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
    );
  };

  const closeModal = () => {
    setShowWinLossModal(false);
  };

  const handleToggleButton = () => {
    setPastderivative15(!pastDerivative15);
  };

  const handleOnClick = async (response: string) => {
    if (response === "Delete") {
      if (!token) {
        alertService.error("Authentication token not found");
        return;
      }
      try {
        const stockName = selectedStock?.symbol.replace(/\.NS$/, "");
        await fetchWrapper.delete(
          `/api/users/watchlists/delete?stock_name=${stockName}`,
          undefined,
          { Authorization: `Bearer ${token}` },
        );

        setRecords((prev) =>
          prev.map((r) =>
            r.symbol.replace(/\.NS$/, "") === stockName
              ? { ...r, added_to_watchlist: false }
              : r,
          ),
        );

        onWatchlistRemoveRef.current?.(stockName as string);
        setConfirmationModal(false);
      } catch (error) {
        alertService.error("Failed to delete portfolio item");
        console.error("Delete error:", error);
      }
    } else {
      setConfirmationModal(false);
    }
  };

  return (
    <div
      className={`border border-gray-200 dark:border-gray-700 shadow-sm w-full rounded-lg ${className}`}
    >
      <style>{`
        :root {
          --flash-bg: #fde047;
          --flash-text: #000000;
          100% {
            background-color: transparent;
            color: inherit;
          }
        }
      `}</style>
      <div className="flex items-center justify-between p-2">
        <div className="flex gap-2 items-center">
          <h3 className="text-sm font-semibold text-black dark:text-white">
            Retest of Order Blocks in Past 15 Days
          </h3>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (user?.is_trial) {
                setFreeTrialRestrictModalPastOrderBlock(true);
              } else {
                setSupplyDemandModalOpen(true);
              }
            }}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors cursor-pointer ml-2"
            title="View Supply & Demand Details"
          >
            <Link2 className="w-4 h-4 transition-colors text-primary hover:text-primary/80" />
          </button>
        </div>
        <div className="flex items-center space-x-2">
          <StockSearchInput
            searchInput={searchInput}
            onSearchChange={onSearchChange}
            setShowDropdown={setShowDropdown}
            showDropdown={showDropdown}
            filteredSymbols={filteredSymbols}
            records={records}
            handleSuggestionClick={handleSuggestionClick}
            freeUser={user?.is_trial}
            setFreeTrialRestrictModal={setFreeTrialRestrictModal}
            dropdownId="retest-order-search-dropdown"
          />
          <span className="text-xs text-gray-600 dark:text-gray-300">
            Derivatives
          </span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={pastDerivative15}
              onChange={handleToggleButton}
            />
            <div
              className="
                w-9 h-5 bg-gray-200 rounded-full peer-focus:outline-none dark:bg-gray-700
                peer-checked:bg-blue-600
                after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                after:bg-white after:border-gray-300 after:border after:rounded-full
                after:h-4 after:w-4 after:transition-all
                peer-checked:after:translate-x-full peer-checked:after:border-white
              "
            />
          </label>
          <span className="text-xs text-gray-600 dark:text-gray-300">
            Non-Derivatives
          </span>
        </div>
      </div>
      <div className="overflow-x-auto relative h-[calc(100%-60px)] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#f4f5f8] dark:bg-[#30324e] sticky top-0 z-10">
            <tr>
              <th
                className="py-2 px-2 text-start text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree group cursor-pointer select-none min-w-[120px]"
                onClick={() => handleSort("symbol")}
              >
                <div className="flex items-center gap-1">
                  <span className="w-4 h-4 flex-shrink-0"></span>
                  <span>Symbol</span>
                  {getSortIcon("symbol")}
                </div>
              </th>
              <th
                className="py-2 px-2 text-start text-sm w-[13%] font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree group cursor-pointer select-none"
                onClick={() => handleSort("percentage")}
              >
                <div className="flex justify-end items-center">
                  Price
                  {getSortIcon("percentage")}
                </div>
              </th>
              <th
                className="py-2 px-2 text-start text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree group cursor-pointer select-none"
                onClick={() => handleSort("ob_date")}
              >
                <div className="flex items-center justify-end">
                  OB Date
                  {getSortIcon("ob_date")}
                </div>
              </th>
              <th
                className="py-2 px-2 text-center text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree group cursor-pointer select-none"
                onClick={() => handleSort("retest_date")}
              >
                <div className="flex items-center justify-end">
                  ReTest Date
                  {getSortIcon("retest_date")}
                </div>
              </th>
              <th
                className="py-2 px-2 text-center text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree group cursor-pointer select-none"
                onClick={() => handleSort("no_of_days")}
              >
                <div className="flex items-center justify-center">
                  # of Days
                  {getSortIcon("no_of_days")}
                </div>
              </th>
              <th
                className="py-2 px-4 sm:px-6 text-center text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree group cursor-pointer select-none"
                onClick={() => handleSort("side")}
              >
                <div className="flex items-center justify-center">
                  Direction
                  {getSortIcon("side")}
                </div>
              </th>
              <th className="py-2 px-2 text-center text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree">
                Win Ratio
              </th>
              <th className="py-2 px-2 text-center text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <RetestOrderSkeleton />
            ) : sortedData.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-12 text-center">
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <p className="text-lg font-medium text-gray-600 dark:text-gray-400">
                      No records found
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-500">
                      Try adjusting toggle
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              sortedData.map((row) => {
                const rowSymbol = row.symbol.replace(/\.NS$/, "");
                return (
                  <RetestRow
                    key={`${rowSymbol}-${row.ob_date}`}
                    row={row}
                    isSelected={
                      selectedRow?.symbol === rowSymbol &&
                      selectedRow?.ob_date === row.ob_date
                    }
                    isTrialUser={isTrialUser}
                    onRowSelect={stableOnRowSelect}
                    onPortfolioClick={handlePortfolioClick}
                    onWatchlistClick={handleWatchlistClick}
                    onWinLossClick={handleWinLossClick}
                    onTrialRestrict={handleTrialRestrict}
                  />
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {showWinLossModal && (
        <WinLossChartModal
          isOpen={showWinLossModal}
          onClose={closeModal}
          stock={winLossRatioData}
          darkMode={darkMode}
          selectedSymbol={selectedSymbolName}
          winLossLoader={winLossLoader}
        />
      )}
      {isModalOpen && selectedStock && (
        <DialogModal
          title=""
          message={
            <ListModal
              heading="Add to Portfolio"
              investmentImg={InvestmentIcon}
              investmentAltText={"Investment"}
              notes={notes}
              selectedStock={selectedStock}
              quantity={quantity}
              updatedStock={updatedStock}
              setQuantity={setQuantity}
              buyPrice={buyPrice}
              setBuyPrice={setBuyPrice}
              setNotes={setNotes}
              isDashboard={true}
              selected_date={selectedDate!}
              getData={async () => {
                setRecords((prev) =>
                  prev.map((r) =>
                    r.symbol.replace(/\.NS$/, "") === selectedStock?.symbol
                      ? { ...r, added_to_portfolio: true }
                      : r,
                  ),
                );
              }}
              handleOnClick={() => {
                setIsModalOpen(false);
                handleResetField();
              }}
              isModalOpen={isModalOpen}
            />
          }
          isModalList={true}
          noBackdrop={true}
          show={isModalOpen}
          setShow={setIsModalOpen}
          handleOnClick={() => {
            setIsModalOpen(false);
            handleResetField();
          }}
        />
      )}
      {isOpen && selectedStock && (
        <DialogModal
          title=""
          message={
            <ListModal
              heading="Add to Watchlist"
              isDashboard={true}
              isWatchlist={true}
              notes={notes}
              updatedStock={updatedStock}
              selectedStock={selectedStock}
              setNotes={setNotes}
              handleOnClick={() => {
                setIsOpen(false);
                handleResetField();
              }}
              watchlist={watchlist}
              setWatchlist={setWatchlist}
              getData={async () => {
                setRecords((prev) =>
                  prev.map((r) =>
                    r.symbol.replace(/\.NS$/, "") === selectedStock?.symbol
                      ? { ...r, added_to_watchlist: true }
                      : r,
                  ),
                );
              }}
            />
          }
          isModalList={true}
          show={isOpen}
          setShow={setIsOpen}
          handleOnClick={() => {
            setIsOpen(false);
            handleResetField();
          }}
          noBackdrop={true}
        />
      )}
      {confirmationModal && (
        <ConfirmationModal
          title={"Remove Stock from WatchList"}
          message={"Are you sure you want to remove stock from WatchList?"}
          show={confirmationModal}
          setShow={setConfirmationModal}
          isDeleteModal={true}
          handleOnClick={handleOnClick}
        />
      )}
      <FreeTrialRestrictionModal
        isOpen={freeTrialRestrictModal}
        onClose={() => setFreeTrialRestrictModal(false)}
        variant="orderBlockRetest"
      />
      <FreeTrialRestrictionModal
        isOpen={freeTrialRestrictModalPastOrderBlock}
        onClose={() => setFreeTrialRestrictModalPastOrderBlock(false)}
        variant="orderBlock"
      />
      {supplyDemandModalOpen && (
        <DialogModal
          title="Supply & Demand Analysis"
          message={
            <SupplyDemandTable
              type="retest"
              derivativeType={!pastDerivative15}
            />
          }
          show={supplyDemandModalOpen}
          setShow={setSupplyDemandModalOpen}
          handleOnClick={() => setSupplyDemandModalOpen(false)}
          maxWidth="max-w-4xl"
        />
      )}
    </div>
  );
};

export default RetestOrderTable;
